/**
 * Color helpers for the LLM usage statistics charts.
 *
 * Two colouring strategies live here:
 *
 * - **Hierarchical (sunburst):** a hue flows down the tree. The root ring (org)
 *   gets a stable hue seeded from its uid; each child drifts its parent's hue by
 *   a seeded offset, so a user reads as a distinct shade *within its org's*
 *   colour family, and the input/output/cached leaves shade that user's hue.
 * - **Categorical (over-time legend):** colours are spaced by golden-angle
 *   rotation indexed by position, which guarantees neighbouring series stay far
 *   apart on the hue wheel — hashing strings to a hue does not.
 *
 * Plotly accepts any CSS colour string, so we emit `hsl(...)` directly.
 */

/** Base saturation / lightness for a seeded colour. */
const BASE_SATURATION = 62;
const BASE_LIGHTNESS = 55;

/** Lightness deltas (percentage points) for the token-type shading. */
const TOKEN_TYPE_LIGHTNESS_DELTA: Record<TokenType, number> = {
  input: 0,
  output: -14,
  cached: 16,
};

/** Lightness added per sunburst ring so inner rings read as grouped. */
const DEPTH_LIGHTNESS_STEP = 6;

/** Max hue drift (deg) a child may have from its parent, indexed by ring depth. */
const CHILD_HUE_SPREAD = [0, 45, 24, 14];

/** Golden-angle rotation — spaces categorical colours maximally apart. */
const GOLDEN_ANGLE = 137.508;

/** Clamp lightness into a readable band so nothing turns pure black/white. */
const MIN_LIGHTNESS = 22;
const MAX_LIGHTNESS = 82;

export type TokenType = 'input' | 'output' | 'cached';

/**
 * Stable, well-distributed 32-bit string hash (FNV-1a + an avalanche finalizer).
 *
 * The avalanche step matters here: org uids often differ only in their last
 * character (e.g. `…0000` vs `…0001`). A naive rolling hash would map those to
 * near-identical values — and thus near-identical hues — so we mix the bits so
 * a single-character change scatters across the full output range.
 */
export function hashString(seed: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index++) {
    hash ^= seed.codePointAt(index) ?? 0;
    hash = Math.imul(hash, 0x01000193);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x45d9f3b);
  hash ^= hash >>> 16;
  return hash >>> 0;
}

/** Stable hue (0-359) for a seed string. */
export function seededHue(seed: string): number {
  return hashString(seed) % 360;
}

function clampLightness(lightness: number): number {
  return Math.min(MAX_LIGHTNESS, Math.max(MIN_LIGHTNESS, lightness));
}

function hslString(hue: number, saturation: number, lightness: number): string {
  return `hsl(${Math.round(((hue % 360) + 360) % 360)}, ${Math.round(
    saturation
  )}%, ${Math.round(clampLightness(lightness))}%)`;
}

/** Build a colour from an explicit hue. */
export function colorFromHue(
  hue: number,
  options: { lightness?: number; saturation?: number } = {}
): string {
  const { lightness = BASE_LIGHTNESS, saturation = BASE_SATURATION } = options;
  return hslString(hue, saturation, lightness);
}

/**
 * Hue for one of `count` siblings, spread evenly across `[-maxSpread, +maxSpread]`
 * around `parentHue`. Even spacing (rather than a random per-seed drift)
 * guarantees siblings are maximally separated — two siblings land on opposite
 * band edges instead of wherever their hashes happen to collide.
 */
export function spreadHue(
  parentHue: number,
  maxSpread: number,
  index: number,
  count: number
): number {
  if (count <= 1 || maxSpread <= 0) return parentHue;
  const t = index / (count - 1); // 0..1 across the sibling set
  return parentHue - maxSpread + 2 * maxSpread * t;
}

/** Max hue drift a child at the given ring depth (0 = root) may take. */
export function childHueSpread(depth: number): number {
  return CHILD_HUE_SPREAD[depth] ?? 10;
}

/** Lightness for a sunburst ring at the given depth. */
export function lightnessForDepth(depth: number): number {
  return BASE_LIGHTNESS + depth * DEPTH_LIGHTNESS_STEP;
}

/** Shade a hue darker/brighter for the input/output/cached token type. */
export function shadeHueForTokenType(hue: number, tokenType: TokenType): string {
  return hslString(hue, BASE_SATURATION, BASE_LIGHTNESS + TOKEN_TYPE_LIGHTNESS_DELTA[tokenType]);
}

/** Evenly-spaced categorical colour by position (golden-angle hue rotation). */
export function categoricalColor(index: number): string {
  return hslString(index * GOLDEN_ANGLE, BASE_SATURATION, BASE_LIGHTNESS);
}
